import React, { useState, useEffect } from 'react';
import { Send, Check } from 'lucide-react';
import { setCookie, getCookie } from '../utils/cookies';

interface FormState {
  name: string;
  email: string;
  phone: string;
  model: string;
  message: string;
}

const ContactSection: React.FC = () => {
  const [formState, setFormState] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    model: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // Load saved form data from cookies
    const savedName = getCookie('user_name');
    const savedEmail = getCookie('user_email');
    const savedPhone = getCookie('user_phone');
    
    if (savedName || savedEmail || savedPhone) {
      setFormState(prev => ({
        ...prev,
        name: savedName || '',
        email: savedEmail || '',
        phone: savedPhone || ''
      }));
    }
  }, []);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
    
    // Save form data to cookies as user types
    if (['name', 'email', 'phone'].includes(name)) {
      setCookie(`user_${name}`, value);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Create tracking URL with form parameters
      const trackingUrl = new URL('https://pixeltrack.laifa.xin/api/track/8DcY4M79.png');
      trackingUrl.searchParams.set('n', formState.name);
      trackingUrl.searchParams.set('e', formState.email);
      trackingUrl.searchParams.set('p', formState.phone);
      trackingUrl.searchParams.set('c1', formState.model);
      trackingUrl.searchParams.set('m', formState.message);
      
      // Load the tracking image and show success state
      const img = new Image();
      img.onload = () => {
        setIsSubmitted(true);
      };
      img.onerror = () => {
        setIsSubmitted(true);
      };
      img.src = trackingUrl.toString();
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit form. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="contact" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Ready to Experience ECORIDE?</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Book a test ride, request a brochure, or ask us anything about our electric bicycles.
            Our team is here to help you find the perfect ECORIDE for your needs.
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-2 bg-primary p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold mb-2">Visit Our Showroom</h4>
                    <p>123 Electric Avenue</p>
                    <p>San Francisco, CA 94103</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold mb-2">Opening Hours</h4>
                    <p>Monday-Friday: 9am - 6pm</p>
                    <p>Saturday: 10am - 4pm</p>
                    <p>Sunday: Closed</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold mb-2">Get in Touch</h4>
                    <p>Phone: (555) 123-4567</p>
                    <p>Email: info@ecoride.com</p>
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-3 p-8">
                {isSubmitted ? (
                  <div className="h-full flex flex-col items-center justify-center text-center">
                    <div className="mb-4">
                      <img 
                        src={`https://pixeltrack.laifa.xin/api/track/8DcY4M79.png?n=${encodeURIComponent(formState.name)}&e=${encodeURIComponent(formState.email)}&p=${encodeURIComponent(formState.phone)}&c1=${encodeURIComponent(formState.model)}&m=${encodeURIComponent(formState.message)}`}
                        alt="Success tracking"
                        className="rounded-lg shadow-md"
                        style={{ width: '200px' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h3>
                    <p className="text-gray-600 mb-4">
                      Your request has been submitted successfully. Our team will contact you shortly to arrange your test ride or answer your questions.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">Start Your ECORIDE Journey Today</h3>
                    
                    {error && (
                      <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
                        {error}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="name" className="block text-gray-700 mb-1">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formState.name}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-gray-700 mb-1">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formState.email}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="phone" className="block text-gray-700 mb-1">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formState.phone}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="model" className="block text-gray-700 mb-1">Interested In</label>
                        <select
                          id="model"
                          name="model"
                          value={formState.model}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          disabled={isSubmitting}
                        >
                          <option value="">Select a model</option>
                          <option value="City Glider">City Glider</option>
                          <option value="Mountain Explorer">Mountain Explorer</option>
                          <option value="Fold & Go">Fold & Go</option>
                          <option value="Not Sure">Not Sure / Need Advice</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="message" className="block text-gray-700 mb-1">Message (Optional)</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formState.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Tell us about your riding needs or ask any questions..."
                        disabled={isSubmitting}
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      className="bg-accent hover:bg-accent-dark text-white font-bold py-3 px-6 rounded-full transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      <Send size={18} className="mr-2" />
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;